import { NgIconComponent } from "@ng-icons/core";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  forwardRef,
  inject,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { OVERLAY_Z_INDEX_CEILING } from "../eva-dialog/eva-z-layers";
import { isNgIconName } from "../utils/icon";

/**
 * Interface pour les presets de date (selections rapides)
 */
export interface DatePreset {
  /** Label affiche dans le bouton */
  label: string;
  /** Fonction qui retourne la date ou la plage de dates */
  getValue: () => Date | Date[];
  /** Icone optionnelle (classe PrimeIcons) */
  icon?: string;
}

/**
 * EVA Datepicker - Composant 100% custom
 *
 * Sélecteur de date avec calendrier utilisant les tokens EVA (pas de PrimeNG).
 * Format suisse (dd.MM.yyyy), semaine commence le lundi.
 *
 * @example
 * // Date simple
 * <eva-datepicker
 *   label="Date de début"
 *   [(value)]="startDate"
 * ></eva-datepicker>
 *
 * // Avec contraintes
 * <eva-datepicker
 *   label="Date de livraison"
 *   [minDate]="today"
 *   [maxDate]="maxDeliveryDate"
 *   [(value)]="deliveryDate"
 * ></eva-datepicker>
 *
 * // Range picker
 * <eva-datepicker
 *   label="Période"
 *   [range]="true"
 *   [(value)]="dateRange"
 * ></eva-datepicker>
 */
@Component({
  selector: "eva-datepicker",
  standalone: true,
  imports: [NgIconComponent, CommonModule],
  templateUrl: "./eva-datepicker.component.html",
  styleUrls: ["./eva-datepicker.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EvaDatepickerComponent),
      multi: true,
    },
  ],
})
export class EvaDatepickerComponent implements ControlValueAccessor, OnChanges, OnDestroy, OnInit {
  protected readonly isNgIconName = isNgIconName;

  /** Registre des datepickers actuellement ouverts — permet de fermer les autres à l'ouverture. */
  private static openInstances = new Set<EvaDatepickerComponent>();

  // ==========================================================================
  // Inputs
  // ==========================================================================

  /** Label affiché au-dessus du champ */
  @Input() label: string = "";

  /** Placeholder du champ */
  @Input() placeholder: string = "jj.mm.aaaa";

  /** Désactive le datepicker */
  @Input() disabled: boolean = false;

  /** Ajoute un * au label */
  @Input() required: boolean = false;

  /** Message d'erreur */
  @Input() error: string = "";

  /** Texte d'aide */
  @Input() helper: string = "";

  /** Taille du champ */
  @Input() size: "small" | "medium" | "large" = "medium";

  /** Date minimum sélectionnable */
  @Input() minDate: Date | null = null;

  /** Date maximum sélectionnable */
  @Input() maxDate: Date | null = null;

  /** Mode range (sélection de période) */
  @Input() range: boolean = false;

  /** Affiche le bouton clear */
  @Input() clearable: boolean = true;

  /** Format d'affichage de la date */
  @Input() dateFormat: string = "dd.MM.yyyy";

  /** Premier jour de la semaine (0=dimanche, 1=lundi) */
  @Input() firstDayOfWeek: number = 1;

  /** Affiche une colonne de numéros de semaine ISO 8601 à gauche du calendrier */
  @Input() showWeek: boolean = false;

  /** ID unique */
  @Input() inputId: string = `eva-datepicker-${Math.random().toString(36).substr(2, 9)}`;

  /** Où attacher le calendrier ('body' pour éviter les overflow:hidden) */
  @Input() appendTo: "body" | null = "body";

  /** Affiche les boutons de sélection rapide (presets) */
  @Input() showPresets: boolean = false;

  /** Liste des presets personnalisés. Si non fourni, utilise les presets par défaut */
  @Input() presets: DatePreset[] | null = null;

  /** Calendrier toujours affiché, sans champ de saisie (ex. dans un popover) */
  @Input() inline: boolean = false;

  // ==========================================================================
  // ViewChild
  // ==========================================================================

  @ViewChild("triggerRef") triggerRef!: ElementRef<HTMLElement>;
  @ViewChild("calendarRef") calendarRef!: ElementRef<HTMLElement>;

  // ==========================================================================
  // Outputs
  // ==========================================================================

  /** Émis quand la date sélectionnée change — pour le two-way binding [(value)] */
  @Output() valueChange = new EventEmitter<Date | Date[] | null>();

  /** Émis quand le calendrier s'ouvre */
  @Output() onOpen = new EventEmitter<void>();

  /** Émis quand le calendrier se ferme */
  @Output() onClose = new EventEmitter<void>();

  // ==========================================================================
  // Internal state
  // ==========================================================================

  _value: Date | Date[] | null = null;
  isOpen: boolean = false;
  isFocused: boolean = false;
  currentMonth: Date = new Date();
  hoverDate: Date | null = null;
  /** Texte brut affiché dans l'input (permet la saisie libre format dd.MM.yyyy). */
  inputText: string = "";

  // Noms des jours (format suisse - commence le lundi)
  weekDays: string[] = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

  // Noms des mois
  monthNames: string[] = [
    "Janvier",
    "Fevrier",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Aout",
    "Septembre",
    "Octobre",
    "Novembre",
    "Decembre",
  ];

  // Presets par defaut
  private defaultPresets: DatePreset[] = [];
  private defaultRangePresets: DatePreset[] = [];

  // Portal element reference
  private calendarElement: HTMLElement | null = null;
  private resizeListener: (() => void) | null = null;
  private scrollListener: (() => void) | null = null;
  /** Listener click en phase capture (contourne stopPropagation des ancêtres). */
  private documentClickCapture: ((e: Event) => void) | null = null;
  private readonly zone = inject(NgZone);

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
  ) {
    this.currentMonth = new Date();
  }

  ngOnInit(): void {
    this.initDefaultPresets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Le template affiche `inputText`, recalculé uniquement par syncInputText(). Le hook couvre
    // aussi le premier rendu : `[value]` est appliqué avant `[dateFormat]` dans le même cycle,
    // donc son setter formate avec le `dd.MM.yyyy` par défaut et le format demandé arrive trop tard.
    if (changes["dateFormat"]) {
      this.syncInputText();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    EvaDatepickerComponent.openInstances.delete(this);
    this.removeCalendarFromBody();
    this.unbindDocumentClickCapture();
  }

  // Capture contre les stopPropagation ; posé ouvert et hors zone, sinon 1 passe CD par clic.
  private bindDocumentClickCapture(): void {
    this.unbindDocumentClickCapture();
    this.documentClickCapture = (event: Event) => this.handleDocumentClickCapture(event);
    this.zone.runOutsideAngular(() => document.addEventListener("click", this.documentClickCapture!, true));
  }

  private unbindDocumentClickCapture(): void {
    if (this.documentClickCapture) {
      document.removeEventListener("click", this.documentClickCapture, true);
      this.documentClickCapture = null;
    }
  }

  private handleDocumentClickCapture(event: Event): void {
    if (!this.isOpen) return;
    const target = event.target as HTMLElement;
    const isInsideComponent = this.elementRef.nativeElement.contains(target);
    const isInsideCalendar = this.calendarElement?.contains(target) ?? false;
    if (!isInsideComponent && !isInsideCalendar) {
      this.zone.run(() => this.closeCalendar());
    }
  }

  /**
   * Initialise les presets par defaut.
   * Appele dynamiquement pour avoir des dates toujours a jour.
   */
  private initDefaultPresets(): void {
    const now = new Date();
    const currentYear = now.getFullYear();

    // Presets pour mode date simple
    this.defaultPresets = [
      {
        label: "Aujourd'hui",
        getValue: () => new Date(),
        icon: "heroCalendar",
      },
      {
        label: "Demain",
        getValue: () => {
          const d = new Date();
          d.setDate(d.getDate() + 1);
          return d;
        },
      },
      {
        label: "Dans 1 semaine",
        getValue: () => {
          const d = new Date();
          d.setDate(d.getDate() + 7);
          return d;
        },
      },
      {
        label: "Dans 1 mois",
        getValue: () => {
          const d = new Date();
          d.setMonth(d.getMonth() + 1);
          return d;
        },
      },
    ];

    // Presets pour mode range (periodes)
    this.defaultRangePresets = [
      {
        label: "Aujourd'hui",
        getValue: () => {
          const today = new Date();
          return [today, today];
        },
        icon: "heroCalendar",
      },
      {
        label: "Cette semaine",
        getValue: () => {
          const today = new Date();
          const dayOfWeek = today.getDay();
          // Lundi = jour 1 (en Suisse la semaine commence le lundi)
          const monday = new Date(today);
          monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
          const sunday = new Date(monday);
          sunday.setDate(monday.getDate() + 6);
          return [monday, sunday];
        },
      },
      {
        label: "Ce mois",
        getValue: () => {
          const today = new Date();
          const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
          const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          return [firstDay, lastDay];
        },
      },
      {
        label: `Annee ${currentYear}`,
        getValue: () => {
          return [new Date(currentYear, 0, 1), new Date(currentYear, 11, 31)];
        },
      },
      {
        label: `Annee ${currentYear - 1}`,
        getValue: () => {
          const year = currentYear - 1;
          return [new Date(year, 0, 1), new Date(year, 11, 31)];
        },
      },
      {
        label: `Annee ${currentYear + 1}`,
        getValue: () => {
          const year = currentYear + 1;
          return [new Date(year, 0, 1), new Date(year, 11, 31)];
        },
      },
      {
        label: "3 derniers mois",
        getValue: () => {
          const today = new Date();
          const threeMonthsAgo = new Date(today);
          threeMonthsAgo.setMonth(today.getMonth() - 3);
          return [threeMonthsAgo, today];
        },
      },
    ];
  }

  // ==========================================================================
  // Two-way binding [(value)]
  // ==========================================================================

  @Input()
  get value(): Date | Date[] | null {
    return this._value;
  }
  set value(val: Date | Date[] | null) {
    if (val !== this._value) {
      this._value = val;
      if (val && !this.range && val instanceof Date) {
        this.currentMonth = new Date(val.getFullYear(), val.getMonth(), 1);
      }
      this.syncInputText();
    }
  }

  // ==========================================================================
  // ControlValueAccessor
  // ==========================================================================

  private onChange: (value: Date | Date[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: Date | Date[] | null): void {
    this._value = value;
    if (value && !this.range && value instanceof Date) {
      this.currentMonth = new Date(value.getFullYear(), value.getMonth(), 1);
    }
    this.syncInputText();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: Date | Date[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Computed
  // ==========================================================================

  get displayValue(): string {
    if (!this._value) return "";

    if (this.range && Array.isArray(this._value)) {
      const [start, end] = this._value;
      if (start && end) {
        return `${this.formatDate(start)} - ${this.formatDate(end)}`;
      } else if (start) {
        return `${this.formatDate(start)} - ...`;
      }
      return "";
    }

    if (this._value instanceof Date) {
      return this.formatDate(this._value);
    }

    return "";
  }

  get hasValue(): boolean {
    if (this.range && Array.isArray(this._value)) {
      return this._value.length > 0 && this._value[0] !== null;
    }
    return this._value !== null;
  }

  get showClear(): boolean {
    return this.clearable && this.hasValue && !this.disabled;
  }

  get currentMonthLabel(): string {
    return `${this.monthNames[this.currentMonth.getMonth()]} ${this.currentMonth.getFullYear()}`;
  }

  get calendarDays(): (Date | null)[][] {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    // Premier jour du mois
    const firstDay = new Date(year, month, 1);
    // Dernier jour du mois
    const lastDay = new Date(year, month + 1, 0);

    // Jour de la semaine du premier jour (ajuste pour lundi=0)
    let startDayOfWeek = firstDay.getDay() - this.firstDayOfWeek;
    if (startDayOfWeek < 0) startDayOfWeek += 7;

    const days: (Date | null)[][] = [];
    let week: (Date | null)[] = [];

    // Jours vides avant le debut du mois
    for (let i = 0; i < startDayOfWeek; i++) {
      week.push(null);
    }

    // Jours du mois
    for (let day = 1; day <= lastDay.getDate(); day++) {
      week.push(new Date(year, month, day));

      if (week.length === 7) {
        days.push(week);
        week = [];
      }
    }

    // Jours vides apres la fin du mois
    if (week.length > 0) {
      while (week.length < 7) {
        week.push(null);
      }
      days.push(week);
    }

    return days;
  }

  /**
   * Retourne les presets a afficher (personnalises ou par defaut selon le mode)
   */
  get activePresets(): DatePreset[] {
    if (this.presets) {
      return this.presets;
    }
    return this.range ? this.defaultRangePresets : this.defaultPresets;
  }

  /**
   * Verifie si un preset est actuellement selectionne
   */
  isPresetActive(preset: DatePreset): boolean {
    if (!this._value) return false;

    const presetValue = preset.getValue();

    if (this.range && Array.isArray(this._value) && Array.isArray(presetValue)) {
      const [start, end] = this._value;
      const [presetStart, presetEnd] = presetValue;
      if (!start || !end) return false;
      return this.isSameDay(start, presetStart) && this.isSameDay(end, presetEnd);
    }

    if (!this.range && this._value instanceof Date && presetValue instanceof Date) {
      return this.isSameDay(this._value, presetValue);
    }

    return false;
  }

  /**
   * Selectionne un preset
   */
  selectPreset(preset: DatePreset): void {
    const value = preset.getValue();

    if (this.range && Array.isArray(value)) {
      // Verifier les contraintes min/max
      let [start, end] = value;
      if (this.minDate && start < this.minDate) start = this.minDate;
      if (this.maxDate && end > this.maxDate) end = this.maxDate;
      this._value = [start, end];
    } else if (!this.range && value instanceof Date) {
      // Verifier les contraintes min/max
      let date = value;
      if (this.minDate && date < this.minDate) date = this.minDate;
      if (this.maxDate && date > this.maxDate) date = this.maxDate;
      this._value = date;
    }

    this.syncInputText();
    this.onChange(this._value);
    this.valueChange.emit(this._value);
    this.closeCalendar();
    this.cdr.markForCheck();
  }

  // ==========================================================================
  // Methods
  // ==========================================================================

  /**
   * Numéro de semaine ISO 8601 (norme suisse : semaine au lundi, règle du jeudi).
   * Implémentation pure et autonome — doit rester identique à moment().isoWeek()
   * pour rester cohérente avec le badge "S26" du planning.
   */
  getIsoWeek(date: Date): number {
    // Copie normalisée à minuit local (pas de mutation de l'entrée)
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    // ISO : lundi=0 … dimanche=6 (getDay() renvoie dimanche=0)
    const dayNr = (d.getDay() + 6) % 7;
    // On se place sur le jeudi de la semaine courante (il décide de l'année ISO)
    d.setDate(d.getDate() - dayNr + 3);
    // Jeudi de la semaine 1 = semaine contenant le 4 janvier
    const firstThursday = new Date(d.getFullYear(), 0, 4);
    const firstThursdayDayNr = (firstThursday.getDay() + 6) % 7;
    firstThursday.setDate(firstThursday.getDate() - firstThursdayDayNr + 3);
    // Nombre de semaines entre les deux jeudis
    const msPerWeek = 7 * 24 * 3600 * 1000;
    return 1 + Math.round((d.getTime() - firstThursday.getTime()) / msPerWeek);
  }

  /** N° de semaine ISO d'une ligne du calendrier (1ère date non-null), ou null si ligne vide. */
  getWeekNumber(week: (Date | null)[]): number | null {
    const ref = week.find((d): d is Date => d !== null);
    return ref ? this.getIsoWeek(ref) : null;
  }

  formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const yearShort = String(year).slice(-2);
    // Respecter le format demandé via @Input() dateFormat (tokens: dd, MM, yyyy, yy)
    // Ordre important : yyyy avant yy pour éviter le double remplacement
    return this.dateFormat
      .replace(/yyyy/g, String(year))
      .replace(/yy/g, yearShort)
      .replace(/dd/g, day)
      .replace(/MM/g, month);
  }

  toggleCalendar(): void {
    if (this.disabled) return;

    if (this.isOpen) {
      this.closeCalendar();
    } else {
      this.openCalendar();
    }
  }

  openCalendar(): void {
    if (this.disabled || this.isOpen) return;

    // Fermer toutes les autres instances ouvertes (tab, focus, etc. ne fire pas document:click)
    for (const other of EvaDatepickerComponent.openInstances) {
      if (other !== this) other.closeCalendar();
    }
    EvaDatepickerComponent.openInstances.add(this);

    this.isOpen = true;
    this.bindDocumentClickCapture();
    this.onOpen.emit();
    // detectChanges() force le rendu synchrone du *ngIf="isOpen" pour que calendarRef
    // soit disponible immédiatement (markForCheck + setTimeout est insuffisant dans un dialog)
    this.cdr.detectChanges();

    if (this.appendTo === "body") {
      this.appendCalendarToBody();
    }
  }

  closeCalendar(): void {
    if (!this.isOpen) return;

    EvaDatepickerComponent.openInstances.delete(this);
    this.isOpen = false;
    this.unbindDocumentClickCapture();
    this.hoverDate = null;
    this.removeCalendarFromBody();
    this.onClose.emit();
    this.onTouched();
    this.cdr.markForCheck();
  }

  private appendCalendarToBody(): void {
    if (!this.calendarRef?.nativeElement || !this.triggerRef?.nativeElement) return;

    const calendar = this.calendarRef.nativeElement;
    const trigger = this.triggerRef.nativeElement;

    // Sauvegarder la reference
    this.calendarElement = calendar;

    // Deplacer vers body
    this.renderer.appendChild(document.body, calendar);

    // Positionner
    this.updateCalendarPosition();

    // Ajouter les listeners pour repositionner
    this.resizeListener = () => this.updateCalendarPosition();
    this.scrollListener = () => this.updateCalendarPosition();

    window.addEventListener("resize", this.resizeListener);
    window.addEventListener("scroll", this.scrollListener, true);
  }

  private removeCalendarFromBody(): void {
    if (this.calendarElement && this.appendTo === "body") {
      try {
        if (this.calendarElement.parentNode === document.body) {
          this.renderer.removeChild(document.body, this.calendarElement);
        }
      } catch (e) {
        // Element deja supprime
      }
      this.calendarElement = null;
    }

    // Nettoyer les listeners
    if (this.resizeListener) {
      window.removeEventListener("resize", this.resizeListener);
      this.resizeListener = null;
    }
    if (this.scrollListener) {
      window.removeEventListener("scroll", this.scrollListener, true);
      this.scrollListener = null;
    }
  }

  private updateCalendarPosition(): void {
    if (!this.calendarElement || !this.triggerRef?.nativeElement) return;

    const trigger = this.triggerRef.nativeElement;
    const rect = trigger.getBoundingClientRect();
    const calendar = this.calendarElement;

    // Position par defaut : sous le trigger
    let top = rect.bottom + window.scrollY + 4;
    let left = rect.left + window.scrollX;

    // Verifier si le calendrier depasse en bas de l'ecran
    const calendarHeight = calendar.offsetHeight || 320;
    const viewportHeight = window.innerHeight;

    if (rect.bottom + calendarHeight > viewportHeight) {
      // Afficher au-dessus du trigger
      top = rect.top + window.scrollY - calendarHeight - 4;
    }

    // Verifier si le calendrier depasse a droite
    const calendarWidth = calendar.offsetWidth || 280;
    const viewportWidth = window.innerWidth;

    if (rect.left + calendarWidth > viewportWidth) {
      left = viewportWidth - calendarWidth - 16 + window.scrollX;
    }

    this.renderer.setStyle(calendar, "position", "absolute");
    this.renderer.setStyle(calendar, "top", `${top}px`);
    this.renderer.setStyle(calendar, "left", `${left}px`);
    this.renderer.setStyle(calendar, "z-index", String(this.getTopZIndex(calendar) + 1));
  }

  /** Retourne le z-index le plus élevé actuellement dans le DOM. */
  private getTopZIndex(excludedElement?: HTMLElement): number {
    let maxZIndex = 12000;

    document.querySelectorAll<HTMLElement>("body *").forEach((element) => {
      if (element === excludedElement) return;

      const zIndex = Number.parseInt(window.getComputedStyle(element).zIndex, 10);
      if (!Number.isNaN(zIndex) && zIndex > maxZIndex && zIndex < OVERLAY_Z_INDEX_CEILING) {
        maxZIndex = zIndex;
      }
    });

    return maxZIndex;
  }

  previousMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
    this.cdr.markForCheck();
  }

  nextMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.cdr.markForCheck();
  }

  goToToday(): void {
    const today = new Date();
    this.currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    this.selectDate(today);
  }

  selectDate(date: Date): void {
    if (this.isDateDisabled(date)) return;

    if (this.range) {
      this.selectRangeDate(date);
    } else {
      this._value = date;
      this.syncInputText();
      this.onChange(this._value);
      this.valueChange.emit(this._value);
      this.closeCalendar();
    }
    this.cdr.markForCheck();
  }

  private selectRangeDate(date: Date): void {
    const current = Array.isArray(this._value) ? [...this._value] : [];

    if (current.length === 0 || current.length === 2) {
      // Debut d'une nouvelle selection
      this._value = [date];
    } else if (current.length === 1) {
      // Fin de la selection
      const start = current[0];
      if (date < start) {
        this._value = [date, start];
      } else {
        this._value = [start, date];
      }
      this.closeCalendar();
    }

    this.onChange(this._value);
    this.valueChange.emit(this._value);
  }

  isDateSelected(date: Date): boolean {
    if (!this._value) return false;

    if (this.range && Array.isArray(this._value)) {
      return this._value.some((d) => d && this.isSameDay(d, date));
    }

    return this._value instanceof Date && this.isSameDay(this._value, date);
  }

  isDateInRange(date: Date): boolean {
    if (!this.range || !Array.isArray(this._value)) return false;

    const [start, end] = this._value;
    if (!start) return false;

    const endDate = end || this.hoverDate;
    if (!endDate) return false;

    const minDate = start < endDate ? start : endDate;
    const maxDate = start < endDate ? endDate : start;

    return date > minDate && date < maxDate;
  }

  isDateDisabled(date: Date): boolean {
    const day = this.startOfDay(date);
    if (this.minDate && day < this.startOfDay(this.minDate)) return true;
    if (this.maxDate && day > this.startOfDay(this.maxDate)) return true;
    return false;
  }

  isToday(date: Date): boolean {
    return this.isSameDay(date, new Date());
  }

  private startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  private isSameDay(d1: Date, d2: Date): boolean {
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  }

  onDayHover(date: Date | null): void {
    this.hoverDate = date;
    this.cdr.markForCheck();
  }

  clear(event: Event): void {
    event.stopPropagation();
    this._value = this.range ? [] : null;
    this.syncInputText();
    this.onChange(this._value);
    this.valueChange.emit(this._value);
    this.cdr.markForCheck();
  }

  handleFocus(): void {
    this.isFocused = true;
  }

  handleBlur(): void {
    this.isFocused = false;
  }

  // ==========================================================================
  // Input textuel (saisie clavier dd.MM.yyyy)
  // ==========================================================================

  /**
   * Synchronise le texte de l'input avec la valeur sélectionnée.
   * Appelé après chaque changement de `_value` (writeValue, setter, selectDate, clear, preset).
   */
  private syncInputText(): void {
    if (this.range || !(this._value instanceof Date)) {
      this.inputText = "";
      return;
    }
    this.inputText = this.formatDate(this._value);
  }

  /**
   * Click sur le trigger (hors input) → toggle calendrier.
   * Le click sur l'input lui-même est stopPropagation dans le template.
   */
  onTriggerClick(event: Event): void {
    if (this.disabled) return;
    const target = event.target as HTMLElement;
    // Si click sur input : focus gère l'ouverture, ne rien faire ici
    if (target?.tagName === "INPUT") return;
    this.toggleCalendar();
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.inputText = target.value;
  }

  onInputFocus(): void {
    this.isFocused = true;
    if (!this.isOpen && !this.disabled) {
      this.openCalendar();
    }
  }

  onInputBlur(): void {
    this.isFocused = false;
    this.commitInputText();
    this.onTouched();
  }

  handleInputKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case "Enter":
        event.preventDefault();
        this.commitInputText();
        this.closeCalendar();
        break;
      case "Escape":
        event.preventDefault();
        this.syncInputText(); // revert au dernier state valide
        this.closeCalendar();
        break;
    }
  }

  /**
   * Tente de parser le texte saisi. Si valide → met à jour `_value` et le calendrier.
   * Si invalide → revert au texte correspondant à `_value`.
   */
  private commitInputText(): void {
    if (this.range) return; // range : pas de saisie directe

    const trimmed = (this.inputText ?? "").trim();

    // Vide → clear la valeur (si clearable)
    if (!trimmed) {
      if (this._value !== null) {
        this._value = null;
        this.onChange(this._value);
        this.valueChange.emit(this._value);
        this.cdr.markForCheck();
      }
      return;
    }

    const parsed = this.parseInputDate(trimmed);
    if (!parsed || this.isDateDisabled(parsed)) {
      // Invalide ou hors min/max → revert
      this.syncInputText();
      this.cdr.markForCheck();
      return;
    }

    if (!(this._value instanceof Date) || !this.isSameDay(this._value, parsed)) {
      this._value = parsed;
      this.currentMonth = new Date(parsed.getFullYear(), parsed.getMonth(), 1);
      this.onChange(this._value);
      this.valueChange.emit(this._value);
    }
    this.syncInputText(); // reformate proprement (padStart 0, etc.)
    this.cdr.markForCheck();
  }

  /**
   * Parse "dd.MM.yyyy" (accepte aussi / et - comme séparateurs, année 2 chiffres tolérée).
   * Retourne null si parse échoue ou date invalide (ex: 31.02.2024).
   */
  private parseInputDate(str: string): Date | null {
    // Un format sans année (`dd.MM`, utilisé par les périodes récurrentes) affiche une date que
    // l'utilisateur ne peut pas ressaisir telle quelle : exiger l'année faisait échouer la saisie
    // en silence, avec retour à la valeur précédente. On accepte donc jour + mois seuls.
    const attendUneAnnee = /y/i.test(this.dateFormat);
    const match = attendUneAnnee
      ? str.match(/^(\d{1,2})[.\/\-\s](\d{1,2})[.\/\-\s](\d{2,4})$/)
      : str.match(/^(\d{1,2})[.\/\-\s](\d{1,2})$/);
    if (!match) return null;
    let day = parseInt(match[1], 10);
    let month = parseInt(match[2], 10);
    let year = attendUneAnnee ? parseInt(match[3], 10) : this.referenceYearForYearlessInput(day, month);
    if (attendUneAnnee && year < 100) year += year < 30 ? 2000 : 1900;
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const date = new Date(year, month - 1, day);
    // Rejet normalisation automatique (31.02 → 03.03)
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
    return date;
  }

  /**
   * Année à prêter à une saisie sans année : celle de la valeur courante, pour ne pas déplacer
   * la date d'un an à chaque frappe. Le 29.02 est la seule combinaison qui peut ne pas exister
   * cette année-là ; on avance alors jusqu'à la prochaine bissextile plutôt que de rejeter la
   * saisie, l'année n'ayant de toute façon aucun sens pour une période récurrente.
   */
  private referenceYearForYearlessInput(day: number, month: number): number {
    let year = this._value instanceof Date ? this._value.getFullYear() : new Date().getFullYear();
    if (day === 29 && month === 2) {
      while (new Date(year, 1, 29).getDate() !== 29) {
        year++;
      }
    }
    return year;
  }

  handleKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        this.toggleCalendar();
        break;
      case "Escape":
        this.closeCalendar();
        break;
    }
  }

  trackByWeek(index: number): number {
    return index;
  }

  trackByDay(index: number, day: Date | null): string {
    return day ? day.toISOString() : `empty-${index}`;
  }
}
